import type { Employee, TimeEntry } from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import type { DayData, DepartmentData, RosterData } from "../types";

export type Direction = "up" | "down" | "left" | "right";

type EmployeeRow = DepartmentData["employees"][number];

// One navigable row, flattened across departments in render order. Either an employee row or the
// department's unassigned row (employee/employeeId null), which renders after its employees. `days`
// is index-aligned with NavGrid.dates (null where there is no DayData for that date).
export interface NavRow {
    department: DepartmentData;
    employee: EmployeeRow | null;
    departmentId: number;
    employeeId: number | null;
    days: (DayData | null)[];
}

// A position in the grid. entryIndex === -1 means "no entry" (empty day, or right after a
// horizontal move, where _setActive resolves the concrete entry).
export interface Coord {
    rowIndex: number;
    colIndex: number;
    entryIndex: number;
}

// The navigation grid. Reverse lookups (keyed by `${deptId}_${empId}` and date) make resolving the
// ID-based cursor O(1). The active entry is resolved against its own row's day, not a global map:
// with mirrored departments the same entry id appears in several rows.
export interface NavGrid {
    rows: NavRow[];
    dates: DateString[];
    colByDate: Map<DateString, number>;
    rowByKey: Map<string, number>;
}

export interface BuildNavGridOptions {
    filterString: string;
    collapsed: ReadonlySet<number>;
    dateRange: { from: DateString; to: DateString } | null;
    // Department ids the active tab restricts navigation to; undefined navigates all departments
    // (mirrors _filteredDepartments).
    visibleDepartmentIds?: ReadonlySet<number>;
    // Whether each department's unassigned row is shown (and thus navigable) — mirrors the
    // rosterDisplayUnassigned setting (and the minimal-mode exclusion).
    includeUnassigned?: boolean;
    // Injectable for testing; defaults to the live contract-covers-range check.
    isEmployeeInRange?: (employee: Employee) => boolean;
}

// Row key: `${deptId}_${empId}` for employees, `${deptId}_unassigned` for the unassigned row.
// Employee ids are numbers, so the "unassigned" sentinel can't collide.
function rowKey(departmentId: number, employeeId: number | null): string {
    return `${departmentId}_${employeeId ?? "unassigned"}`;
}

// Group unassigned entries into the stacks the row renders (by position, planned time, published
// status). Shared with _renderUnassigned so navigation walks the same entries the UI shows.
export function groupUnassignedStacks(entries: TimeEntry[]): TimeEntry[][] {
    const stacks = new Map<string, TimeEntry[]>();
    for (const entry of entries) {
        const key = `${entry.position?.id}_${entry.startPlanned}_${entry.endPlanned}_${entry.isPublished}`;
        const stack = stacks.get(key);
        if (stack) {
            stack.push(entry);
        } else {
            stacks.set(key, [entry]);
        }
    }
    return [...stacks.values()];
}

function wrapIndex(index: number, length: number): number {
    return ((index % length) + length) % length;
}

// Shown employees: name/staffNumber substring match + a contract covering the range. Mirrors
// _renderDepartment's filter.
function navigableEmployees(
    dep: DepartmentData,
    filter: string,
    isEmployeeInRange: (employee: Employee) => boolean
): EmployeeRow[] {
    return dep.employees.filter(
        ({ employeeData: { employee } }) =>
            `${employee.name} ${employee.staffNumber}`.toLowerCase().includes(filter) && isEmployeeInRange(employee)
    );
}

// Shown when it has visible employees and isn't manually collapsed (collapse ignored while
// filtering — mirrors _renderDepartment).
function isDepartmentNavigable(dep: DepartmentData, employees: EmployeeRow[], opts: BuildNavGridOptions): boolean {
    return employees.length > 0 && !(!opts.filterString && opts.collapsed.has(dep.department.id));
}

function buildRow(dep: DepartmentData, employee: EmployeeRow, dates: DateString[]): NavRow {
    const byDate = new Map<DateString, DayData>();
    for (const day of employee.days) {
        byDate.set(day.date, day);
    }
    return {
        department: dep,
        employee,
        departmentId: dep.department.id,
        employeeId: employee.employeeData.employee.id,
        days: dates.map((date) => byDate.get(date) ?? null),
    };
}

function buildUnassignedRow(dep: DepartmentData, dates: DateString[]): NavRow {
    const byDate = new Map<DateString, DayData>();
    for (const { date, entries } of dep.unassigned) {
        byDate.set(date, {
            date,
            entries: groupUnassignedStacks(entries).map((stack) => stack[0]),
            blocked: false,
            blockedReason: "",
            today: false,
            readonly: false,
            availabilities: [],
            holiday: null,
        });
    }
    return {
        department: dep,
        employee: null,
        departmentId: dep.department.id,
        employeeId: null,
        days: dates.map((date) => byDate.get(date) ?? null),
    };
}

// Flatten the visible roster (departments × employees × days) into the navigation grid. The only
// place the tree is traversed for navigation; callers memoize the result.
export function buildNavGrid(data: RosterData, opts: BuildNavGridOptions): NavGrid {
    const filter = opts.filterString.toLowerCase();
    const isEmployeeInRange =
        opts.isEmployeeInRange ?? ((employee) => !opts.dateRange || !!employee.getContractForRange(opts.dateRange));

    const rows: NavRow[] = [];
    const rowByKey = new Map<string, number>();

    for (const dep of data.departments) {
        if (opts.visibleDepartmentIds && !opts.visibleDepartmentIds.has(dep.department.id)) {
            continue;
        }
        const employees = navigableEmployees(dep, filter, isEmployeeInRange);
        if (!isDepartmentNavigable(dep, employees, opts)) {
            continue;
        }
        for (const employee of employees) {
            const row = buildRow(dep, employee, data.dates);
            rowByKey.set(rowKey(row.departmentId, row.employeeId), rows.length);
            rows.push(row);
        }
        // The unassigned row renders after the department's employees, so it navigates there too.
        if (opts.includeUnassigned) {
            const row = buildUnassignedRow(dep, data.dates);
            rowByKey.set(rowKey(row.departmentId, null), rows.length);
            rows.push(row);
        }
    }

    const colByDate = new Map<DateString, number>();
    data.dates.forEach((date, index) => colByDate.set(date, index));

    return { rows, dates: data.dates, colByDate, rowByKey };
}

export interface ActiveCell {
    date: DateString | null;
    employee: number | null;
    department: number | null;
    entry: string | null;
}

function resolveRowIndex(grid: NavGrid, department: number | null, employee: number | null): number {
    // No active cell at all → first row. A set department with a null employee is the unassigned
    // row (rowKey maps it to the "unassigned" sentinel).
    if (department == null) {
        return 0;
    }
    // Active row no longer visible (e.g. just filtered out) → start from the first row.
    return grid.rowByKey.get(rowKey(department, employee)) ?? 0;
}

function resolveColIndex(grid: NavGrid, date: DateString | null, dir: Direction): number {
    if (date != null) {
        const col = grid.colByDate.get(date);
        if (col != null) {
            return col;
        }
    }
    // No active date: vertical moves stay on the first date; horizontal moves start "unset"
    // (-1) so the next move lands on the first cell.
    return dir === "up" || dir === "down" ? 0 : -1;
}

function resolveEntryIndex(grid: NavGrid, entry: string | null, rowIndex: number, colIndex: number): number {
    if (entry == null) {
        return -1;
    }
    // Find the entry within the resolved cell's own day. A global id→coord map would be wrong: with
    // mirrored departments the same entry id appears in several rows, so a shared map resolves to
    // the wrong row and the cursor can't step through the stack. Returns -1 if not found.
    const day = grid.rows[rowIndex]?.days[colIndex];
    return day ? day.entries.findIndex((e) => e.id === entry) : -1;
}

// Resolve the ID-based active cell to a grid coordinate (O(1) via the reverse lookups). `dir`
// decides the column fallback when there is no active date.
export function resolveCoord(grid: NavGrid, active: ActiveCell, dir: Direction): Coord {
    const rowIndex = resolveRowIndex(grid, active.department, active.employee);
    const colIndex = resolveColIndex(grid, active.date, dir);
    return { rowIndex, colIndex, entryIndex: resolveEntryIndex(grid, active.entry, rowIndex, colIndex) };
}

// Map a coordinate back to the IDs _setActive consumes. An out-of-range entryIndex yields
// entry: null so _setActive resolves the first entry / "new" — except the unassigned row has no
// employee for it to resolve against, so pick its first stack here.
export function coordToCell(
    grid: NavGrid,
    coord: Coord
): { date: DateString; department: number; employee: number | null; entry: string | null } {
    const row = grid.rows[coord.rowIndex];
    const day = row.days[coord.colIndex];
    let entry: string | null = null;
    if (coord.entryIndex >= 0) {
        entry = day?.entries[coord.entryIndex]?.id ?? null;
    } else if (row.employeeId == null) {
        entry = day?.entries[0]?.id ?? null;
    }
    return { date: grid.dates[coord.colIndex], department: row.departmentId, employee: row.employeeId, entry };
}

function entryCount(grid: NavGrid, rowIndex: number, colIndex: number): number {
    return grid.rows[rowIndex].days[colIndex]?.entries.length ?? 0;
}

// Advance past unassigned rows (employeeId == null) in the step direction, wrapping. WASD never
// lands on an unassigned row when crossing into a new one; it stays navigable only for a cell the
// cursor is already on. Falls back to the start when every row is unassigned.
function skipUnassignedRows(grid: NavGrid, rowIndex: number, step: number): number {
    let index = rowIndex;
    for (let i = 0; i < grid.rows.length; i++) {
        if (grid.rows[index].employeeId != null) {
            return index;
        }
        index = wrapIndex(index + step, grid.rows.length);
    }
    return rowIndex;
}

// w/s: step through the day's stacked entries; at a stack end cross to the prev/next row's same
// date (first/last entry), wrapping at the row-list ends.
function moveVertical(grid: NavGrid, coord: Coord, step: number): Coord {
    const colIndex = Math.max(0, coord.colIndex);
    let rowIndex = coord.rowIndex;
    let entryIndex = coord.entryIndex + step;

    if (entryIndex < 0) {
        rowIndex = skipUnassignedRows(grid, wrapIndex(rowIndex - 1, grid.rows.length), -1);
        entryIndex = entryCount(grid, rowIndex, colIndex) - 1;
    } else if (entryIndex > entryCount(grid, rowIndex, colIndex) - 1) {
        rowIndex = skipUnassignedRows(grid, wrapIndex(rowIndex + 1, grid.rows.length), 1);
        entryIndex = 0;
    }

    return { rowIndex, colIndex, entryIndex };
}

// Keep the stack position when moving across days so left/right lands on the visually adjacent
// entry. Clamp to the target day's entries, or -1 when it has none / none was selected.
function clampEntryIndex(grid: NavGrid, rowIndex: number, colIndex: number, entryIndex: number): number {
    const count = entryCount(grid, rowIndex, colIndex);
    if (entryIndex < 0 || count === 0) {
        return -1;
    }
    return Math.min(entryIndex, count - 1);
}

// a/d: step one date; past the last/first date wrap to the next/prev row's first/last cell.
function moveHorizontal(grid: NavGrid, coord: Coord, step: number): Coord {
    if (coord.colIndex < 0) {
        // First selection from no active date → first cell of the current row.
        return {
            rowIndex: coord.rowIndex,
            colIndex: 0,
            entryIndex: clampEntryIndex(grid, coord.rowIndex, 0, coord.entryIndex),
        };
    }

    const last = grid.dates.length - 1;
    let rowIndex = coord.rowIndex;
    let colIndex = coord.colIndex + step;

    if (colIndex > last) {
        rowIndex = skipUnassignedRows(grid, wrapIndex(rowIndex + 1, grid.rows.length), 1);
        colIndex = 0;
    } else if (colIndex < 0) {
        rowIndex = skipUnassignedRows(grid, wrapIndex(rowIndex - 1, grid.rows.length), -1);
        colIndex = last;
    }

    return { rowIndex, colIndex, entryIndex: clampEntryIndex(grid, rowIndex, colIndex, coord.entryIndex) };
}

// Pure cursor movement; never touches the DOM or active state. Returns `coord` unchanged when the
// grid has no rows.
export function move(grid: NavGrid, coord: Coord, dir: Direction): Coord {
    if (!grid.rows.length) {
        return coord;
    }
    return dir === "up" || dir === "down"
        ? moveVertical(grid, coord, dir === "down" ? 1 : -1)
        : moveHorizontal(grid, coord, dir === "right" ? 1 : -1);
}
